"use client";

import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L, { LatLngBoundsExpression, Icon, LatLngExpression } from 'leaflet';
import { Card } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import type { MapPin as MapPinType } from '@/lib/types';
import ReactDOMServer from 'react-dom/server';

// Fix para el icono por defecto de Leaflet que no funciona bien con bundlers
const createLeafletIcon = (selected: boolean) => {
    const iconHtml = ReactDOMServer.renderToString(
      <MapPin 
        className={selected ? "w-8 h-8 text-yellow-400" : "w-8 h-8 text-primary"}
        fill={selected ? "hsl(var(--primary))" : "hsl(var(--primary-foreground))"} 
      />
    );
    return L.divIcon({
        html: iconHtml,
        className: 'bg-transparent border-0',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });
};

const defaultIcon = createLeafletIcon(false);
const selectedIcon = createLeafletIcon(true);

// Componente para manejar el cambio de vista del mapa
function ChangeView({ center, zoom }: { center: LatLngExpression, zoom: number }) {
  const map = useMap();
  map.setView(center, zoom);
  return null;
}

export function InteractiveMap({ locations, onPinClick, selectedPinId }: { locations: MapPinType[], onPinClick?: (pin: MapPinType) => void, selectedPinId?: number }) {
    // Las coordenadas [y, x] para Leaflet son [lat, lng]. Usaremos valores negativos para 'lat' para que el 0,0 esté arriba a la izquierda.
    const center: LatLngExpression = [-512, 512]; 
    const mapBounds: LatLngBoundsExpression = [[0, 0], [-1024, 1024]];

    // State to handle client-side only rendering of Leaflet
    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true);
    }, []);

    if (!isClient) {
        return (
            <Card className="flex items-center justify-center w-full h-[80vh] min-h-[600px] bg-muted">
                <p>Cargando mapa...</p>
            </Card>
        );
    }

    return (
        <Card className="overflow-hidden border-2 border-primary/20 shadow-lg">
            <MapContainer
                center={center}
                zoom={2}
                style={{ height: '80vh', minHeight: '600px', backgroundColor: 'hsl(var(--background))' }}
                crs={L.CRS.Simple} // Usar un sistema de coordenadas simple, no geográfico
                maxBounds={mapBounds}
            >
                <TileLayer
                    url="https://i.imgur.com/A58UrQM.png" // La imagen del mapa
                    bounds={mapBounds}
                    noWrap={true}
                />
                {locations.map((location) => (
                    <Marker
                        key={location.id}
                        position={location.coords as LatLngExpression}
                        icon={selectedPinId === location.id ? selectedIcon : defaultIcon}
                        eventHandlers={{
                            click: () => {
                                onPinClick?.(location);
                            },
                        }}
                    >
                        <Popup>
                            <div className="font-sans">
                                <h3 className="font-bold text-base mb-1">{location.name}</h3>
                                <p className="text-sm">{location.description}</p>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </Card>
    );
}
